import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import type { ConversionPopupComponent as ConversionPopupComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { usePageContext } from '../contexts/PageContext';
import { useStaticPageContext } from '../ssr/StaticPageContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { ButtonLink } from './shared/ButtonLink';
import type { CropData } from './ComponentEditModal/shared/ImageCropModal';
import { getCroppedImageRenderProps } from './ComponentEditModal/shared/cropUtils';
import { resolveConversionPopupOverlay } from '../utils/conversionPopupOverlay';
import {
  mergeComponentEditDraft,
  useComponentEditDraft,
} from '../contexts/ComponentEditDraftContext';

interface ConversionPopupComponentProps {
  component: ConversionPopupComponentType;
  isEditing?: boolean;
}

export type PopupWidgetConfig = ReturnType<typeof buildWidgetConfig>;

function buildWidgetConfig(
  component: ConversionPopupComponentType,
  ctx: { siteId: string; pageId: string; pageSlug: string },
  colors: Record<string, string>
) {
  const d = component.data;
  return {
    contentPurpose: d.contentPurpose ?? 'offer',
    headline: d.headline,
    body: d.body ?? '',
    primaryCtaLabel: d.primaryCtaLabel ?? '',
    primaryCtaUrl: d.primaryCtaUrl ?? '',
    primaryCtaTarget: d.primaryCtaTarget ?? '_self',
    secondaryCtaLabel: d.secondaryCtaLabel ?? '',
    contentAlignment: d.contentAlignment ?? 'left',
    imageUrl: d.imageUrl ?? '',
    crop: d.crop != null ? d.crop : undefined,
    exitIntentDesktop: !!d.exitIntentDesktop,
    timeDelaySeconds:
      d.timeDelaySeconds === undefined || d.timeDelaySeconds === null ? null : d.timeDelaySeconds,
    scrollDepthPercent:
      d.scrollDepthPercent === undefined || d.scrollDepthPercent === null
        ? null
        : d.scrollDepthPercent,
    minDelayAfterLoadMs: d.minDelayAfterLoadMs ?? 5000,
    pageSlugs: Array.isArray(d.pageSlugs) ? d.pageSlugs : [],
    showOncePerSession: d.showOncePerSession !== false,
    excludeAfterConversion: !!d.excludeAfterConversion,
    conversionFormComponentIds: Array.isArray(d.conversionFormComponentIds)
      ? d.conversionFormComponentIds
      : [],
    mobileMode: d.mobileMode ?? 'bottom_sheet',
    maxViewportWidthForMobile: d.maxViewportWidthForMobile ?? 768,
    priority: d.priority ?? 0,
    campaignKey: d.campaignKey ?? null,
    componentId: component.id,
    siteId: ctx.siteId,
    pageSlug: ctx.pageSlug,
    pageId: ctx.pageId,
    colors,
  };
}

function normSlug(s: string) {
  return String(s || '').replace(/^\/+|\/+$/g, '');
}

function pageAllowedForCfg(cfg: PopupWidgetConfig): boolean {
  const slugs = cfg.pageSlugs || [];
  if (!slugs.length) return true;
  const cur = normSlug(cfg.pageSlug || '');
  return slugs.some(s => normSlug(s) === cur);
}

function convExcludedForCfg(cfg: PopupWidgetConfig): boolean {
  if (!cfg.excludeAfterConversion) return false;
  try {
    return (
      localStorage.getItem(`cp_popup_conv_${cfg.siteId || ''}_${cfg.componentId || ''}`) === '1'
    );
  } catch {
    return false;
  }
}

function onceShownForCfg(cfg: PopupWidgetConfig): boolean {
  if (!cfg.showOncePerSession) return false;
  const key = `cp_popup_once_${cfg.siteId || ''}_${cfg.campaignKey || cfg.componentId || 'x'}`;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markOnceForCfg(cfg: PopupWidgetConfig) {
  const key = `cp_popup_once_${cfg.siteId || ''}_${cfg.campaignKey || cfg.componentId || 'x'}`;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

function num(v: unknown, def: number): number {
  if (v == null || v === '') return def;
  const n = Number(v);
  return Number.isNaN(n) ? def : n;
}

function scrollableHeight(): number {
  const doc = document.documentElement;
  const body = document.body;
  const h = Math.max(
    doc?.scrollHeight ?? 0,
    body?.scrollHeight ?? 0,
    doc?.offsetHeight ?? 0,
    body?.offsetHeight ?? 0
  );
  return h - window.innerHeight;
}

function hasAutoTriggers(cfg: PopupWidgetConfig): boolean {
  if (cfg.exitIntentDesktop) return true;
  const tds = num(cfg.timeDelaySeconds, NaN);
  if (!Number.isNaN(tds) && tds >= 0) return true;
  const sdp = num(cfg.scrollDepthPercent, NaN);
  if (!Number.isNaN(sdp) && sdp > 0) return true;
  return false;
}

function autoBlockedOnMobile(cfg: PopupWidgetConfig): boolean {
  if (cfg.mobileMode !== 'disabled') return false;
  return window.innerWidth <= (cfg.maxViewportWidthForMobile || 768);
}

function useConversionPopupLiveTriggers(
  enabled: boolean,
  cfg: PopupWidgetConfig,
  data: ConversionPopupComponentType['data'],
  setOpen: (v: boolean) => void
) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    let cancelled = false;
    let opened = false;

    const tryOpen = () => {
      if (cancelled || opened) return;
      if (!pageAllowedForCfg(cfg)) return;
      if (convExcludedForCfg(cfg)) return;
      if (onceShownForCfg(cfg)) return;
      opened = true;
      if (cfg.showOncePerSession) markOnceForCfg(cfg);
      setOpen(true);
    };

    const onFormSuccess = (e: Event) => {
      const ce = e as CustomEvent<{ pageId?: string; componentId?: string }>;
      const fid = ce.detail?.componentId;
      if (!fid || !cfg.excludeAfterConversion) return;
      const ids = cfg.conversionFormComponentIds || [];
      if (ids.length && !ids.includes(fid)) return;
      try {
        localStorage.setItem(`cp_popup_conv_${cfg.siteId || ''}_${cfg.componentId || ''}`, '1');
      } catch {
        /* ignore */
      }
    };

    document.addEventListener('cp:form-success', onFormSuccess);

    const minDelay = Math.max(0, num(cfg.minDelayAfterLoadMs, 0));
    const timers: number[] = [];
    let scrollHandler: (() => void) | undefined;
    let leaveHandler: ((e: MouseEvent) => void) | undefined;

    const autoEligible =
      pageAllowedForCfg(cfg) &&
      !convExcludedForCfg(cfg) &&
      !onceShownForCfg(cfg) &&
      hasAutoTriggers(cfg) &&
      !autoBlockedOnMobile(cfg);

    if (autoEligible) {
      const tds = num(cfg.timeDelaySeconds, NaN);
      if (!Number.isNaN(tds) && tds >= 0) {
        timers.push(
          window.setTimeout(
            () => {
              if (!cancelled) tryOpen();
            },
            minDelay + tds * 1000
          )
        );
      }

      const sdp = num(cfg.scrollDepthPercent, NaN);
      if (!Number.isNaN(sdp) && sdp > 0) {
        timers.push(
          window.setTimeout(() => {
            if (cancelled) return;
            const onScroll = () => {
              if (cancelled || opened) return;
              const scrollable = scrollableHeight();
              if (scrollable <= 1) {
                window.removeEventListener('scroll', onScroll);
                document.removeEventListener('scroll', onScroll);
                tryOpen();
                return;
              }
              const pct = (window.scrollY / scrollable) * 100;
              if (pct >= sdp - 0.5) {
                window.removeEventListener('scroll', onScroll);
                document.removeEventListener('scroll', onScroll);
                tryOpen();
              }
            };
            scrollHandler = onScroll;
            window.addEventListener('scroll', onScroll, { passive: true });
            document.addEventListener('scroll', onScroll, { passive: true });
            onScroll();
          }, minDelay)
        );
      }

      if (cfg.exitIntentDesktop) {
        let fine = false;
        try {
          fine = window.matchMedia('(pointer: fine)').matches;
        } catch {
          fine = false;
        }
        if (fine && window.innerWidth >= 900) {
          timers.push(
            window.setTimeout(() => {
              if (cancelled) return;
              const onLeave = (e: MouseEvent) => {
                if (cancelled || opened) return;
                if (!e.relatedTarget && e.clientY < 12) {
                  document.removeEventListener('mouseout', onLeave);
                  tryOpen();
                }
              };
              leaveHandler = onLeave;
              document.addEventListener('mouseout', onLeave);
            }, minDelay)
          );
        }
      }
    }

    return () => {
      cancelled = true;
      timers.forEach(id => clearTimeout(id));
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        document.removeEventListener('scroll', scrollHandler);
      }
      if (leaveHandler) {
        document.removeEventListener('mouseout', leaveHandler);
      }
      document.removeEventListener('cp:form-success', onFormSuccess);
    };
  }, [enabled, cfg, data, setOpen]);
}

interface PopupModalProps {
  data: ConversionPopupComponentType['data'];
  colors: Record<string, string>;
  headingFont: React.CSSProperties;
  bodyFont: React.CSSProperties;
  zIndex: number;
  onClose: () => void;
  mobileMode: string;
  maxMobileW: number;
}

const alignToCss = (a: string | undefined): React.CSSProperties['textAlign'] => {
  if (a === 'center' || a === 'right' || a === 'left') return a;
  return 'left';
};

const PopupModal: React.FC<PopupModalProps> = ({
  data,
  colors,
  headingFont,
  bodyFont,
  zIndex,
  onClose,
  mobileMode,
  maxMobileW,
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= maxMobileW;
  const sheet = mobileMode === 'bottom_sheet' && isMobile;
  const full = mobileMode === 'fullscreen' && isMobile;
  const contentAlign = alignToCss(data.contentAlignment);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className={`fixed inset-0 flex p-4 ${sheet ? 'items-end p-0' : 'items-center justify-center'} ${full ? 'p-0' : ''}`}
      style={{ background: colors.overlay, zIndex }}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-h-[90vh] overflow-auto rounded-xl shadow-xl p-6 ${
          sheet ? 'max-w-none rounded-b-none rounded-t-xl max-h-[85vh]' : 'max-w-md'
        } ${full ? 'max-w-none max-h-screen h-full rounded-none' : ''}`}
        style={{ backgroundColor: colors.panelBackground, color: colors.body, ...bodyFont }}
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute top-3 right-3 p-1 rounded-md hover:bg-black/5"
          style={{ color: colors.closeButton }}
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} />
        </button>
        <div style={{ textAlign: contentAlign }}>
          <h2
            className="text-xl font-bold pr-8 mb-2"
            style={{ color: colors.headline, ...headingFont }}
          >
            {data.headline}
          </h2>
          {data.imageUrl
            ? (() => {
                const raw = data.crop;
                const crop = raw === null ? undefined : (raw as CropData | undefined);
                if (crop?.croppedAreaPercent) {
                  const { aspectRatio, imageStyle } = getCroppedImageRenderProps(crop);
                  return (
                    <div
                      className="relative w-full overflow-hidden rounded-lg mb-3"
                      style={{
                        aspectRatio,
                        width: '100%',
                      }}
                    >
                      <img src={data.imageUrl} alt="" style={imageStyle} />
                    </div>
                  );
                }
                return (
                  <img
                    src={data.imageUrl}
                    alt=""
                    className="w-full rounded-lg mb-3 object-cover max-h-48"
                    style={crop?.grayscale ? { filter: 'grayscale(100%)' } : undefined}
                  />
                );
              })()
            : null}
          {data.body ? (
            <p className="text-sm mb-4 whitespace-pre-wrap opacity-90">{data.body}</p>
          ) : null}
          {data.primaryCtaLabel && data.primaryCtaUrl ? (
            <ButtonLink
              url={data.primaryCtaUrl}
              target={data.primaryCtaTarget ?? '_self'}
              className="inline-block px-4 py-2 rounded-lg font-semibold text-center"
              style={{ backgroundColor: colors.buttonBackground, color: colors.buttonText }}
            >
              {data.primaryCtaLabel}
            </ButtonLink>
          ) : null}
          {data.secondaryCtaLabel ? (
            <div className="mt-3">
              <button
                type="button"
                onClick={onClose}
                className="bg-transparent border-0 cursor-pointer px-2 py-1 text-sm underline opacity-70 hover:opacity-100"
                style={{ color: colors.body, ...bodyFont }}
              >
                {data.secondaryCtaLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const ConversionPopupComponent: React.FC<ConversionPopupComponentProps> = ({
  component,
  isEditing = false,
}) => {
  const editDraft = useComponentEditDraft()?.draft ?? null;
  const data = useMemo(
    () =>
      mergeComponentEditDraft(
        component.id,
        component.data as Record<string, unknown>,
        editDraft
      ) as ConversionPopupComponentType['data'],
    [component.id, component.data, editDraft]
  );
  const { currentTheme } = useThemeSafe();
  const customColorsCtx = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColorsCtx || undefined);
  const brandFonts = useBrandFonts();
  const pageCtx = usePageContext();
  const staticCtx = useStaticPageContext();

  const siteId = staticCtx.siteId ?? pageCtx.siteId ?? '';
  const pageSlug = staticCtx.pageSlug ?? pageCtx.pageSlug ?? '';
  const pageId = staticCtx.pageId ?? pageCtx.pageId ?? '';

  const tc = themeStyles.conversionPopup;
  const cc = (data as { customColors?: Record<string, string | undefined> }).customColors;

  const colors = useMemo(
    () => ({
      overlay: resolveConversionPopupOverlay(cc?.overlay, tc?.overlay),
      panelBackground: cc?.panelBackground || tc?.panelBackground || '#ffffff',
      headline: cc?.headline || tc?.headlineColor || '#111827',
      body: cc?.body || tc?.bodyTextColor || '#374151',
      buttonBackground: cc?.buttonBackground || tc?.buttonBackground || '#3b82f6',
      buttonText: cc?.buttonText || tc?.buttonText || '#ffffff',
      closeButton: cc?.closeButton || tc?.closeButton || '#6b7280',
    }),
    [cc, tc]
  );

  const cfg = useMemo(
    () => buildWidgetConfig({ ...component, data }, { siteId, pageId, pageSlug }, colors),
    [component, data, siteId, pageId, pageSlug, colors]
  );

  const encoded = useMemo(() => {
    try {
      return encodeURIComponent(JSON.stringify(cfg));
    } catch {
      return '';
    }
  }, [cfg]);

  const headingFont = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFont = brandFonts?.bodyFont ? { fontFamily: getFontFamily(brandFonts.bodyFont) } : {};

  const [previewOpen, setPreviewOpen] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);

  const setLiveOpenStable = useCallback((v: boolean) => {
    setLiveOpen(v);
  }, []);

  useConversionPopupLiveTriggers(!isEditing, cfg, data, setLiveOpenStable);

  if (isEditing) {
    const triggers: string[] = [];
    if (data.exitIntentDesktop) triggers.push('Exit intent (desktop)');
    if (data.timeDelaySeconds != null && data.timeDelaySeconds >= 0) {
      triggers.push(`Timer ${data.timeDelaySeconds}s`);
    }
    if (data.scrollDepthPercent != null && data.scrollDepthPercent > 0) {
      triggers.push(`Scroll ${data.scrollDepthPercent}%`);
    }

    return (
      <>
        <div className="border border-dashed border-violet-300 bg-violet-50/80 rounded-lg p-4 my-4 pr-16">
          <div>
            <p className="text-sm font-semibold text-violet-900">Conversion popup</p>
            <p className="text-xs text-violet-700 mt-1">
              {data.contentPurpose.replace(/_/g, ' ')} ·{' '}
              {triggers.length ? triggers.join(' · ') : 'No triggers configured'}
            </p>
            <p className="text-xs text-violet-600 mt-1 line-clamp-2">{data.headline}</p>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="mt-3 text-sm px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700"
            >
              Preview popup
            </button>
          </div>
          <p className="text-xs text-violet-600 mt-3">
            In the builder preview, timers and scroll run in React (same rules as the live site).
            Published sites also load{' '}
            <code className="bg-violet-100 px-1 rounded">conversion-popup.js</code>.
          </p>
        </div>

        {previewOpen && (
          <PopupModal
            data={data}
            colors={colors}
            headingFont={headingFont}
            bodyFont={bodyFont}
            zIndex={100}
            onClose={() => setPreviewOpen(false)}
            mobileMode={data.mobileMode ?? 'bottom_sheet'}
            maxMobileW={data.maxViewportWidthForMobile ?? 768}
          />
        )}
      </>
    );
  }

  if (!encoded) return null;

  return (
    <>
      <div
        className="cp-popup-root"
        data-widget="conversion-popup"
        hidden
        aria-hidden="true"
        data-component-id={component.id}
        data-config={encoded}
      />
      {liveOpen && (
        <PopupModal
          data={data}
          colors={colors}
          headingFont={headingFont}
          bodyFont={bodyFont}
          zIndex={99999}
          onClose={() => setLiveOpen(false)}
          mobileMode={data.mobileMode ?? 'bottom_sheet'}
          maxMobileW={data.maxViewportWidthForMobile ?? 768}
        />
      )}
    </>
  );
};
